import React, { useState, useEffect } from 'react';
import {
  Plus,
  Search,
  Filter,
  DollarSign,
  TrendingUp,
  AlertCircle,
  Calendar,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  Download,
  CreditCard,
  Building,
  User,
  RefreshCw,
  MessageCircle,
  Mail,
  Smartphone,
  Trash2,
  Printer,
} from 'lucide-react';
import api from '../lib/api';
import { useAuthStore } from '../store/authStore';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import Modal from '../components/Modal';
import { Input, Select } from '../components/Form';

interface Charge {
  id: string;
  competence: string;
  dueDate: string;
  amount: string | number;
  paidAmount?: string | number;
  status: string;
  paymentDate?: string;
  description?: string;
  lease?: {
    id: string;
    code: string;
    property?: {
      title?: string;
      code: string;
    };
    tenant?: {
      name: string;
    };
  };
}

interface Lease {
  id: string;
  code: string;
  property?: {
    title?: string;
  };
  tenant?: {
    name: string;
  };
}

interface ChargeStats {
  totalPending: number;
  totalOverdue: number;
  totalPaid: number;
  count: {
    pending: number;
    overdue: number;
    paid: number;
  };
}

export default function ChargesPage() {
  const [charges, setCharges] = useState<Charge[]>([]);
  const [leases, setLeases] = useState<Lease[]>([]);
  const [stats, setStats] = useState<ChargeStats>({
    totalPending: 0,
    totalOverdue: 0,
    totalPaid: 0,
    count: { pending: 0, overdue: 0, paid: 0 },
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('ALL');
  const [showModal, setShowModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [selectedChargeForSend, setSelectedChargeForSend] = useState<Charge | null>(null);
  const [formData, setFormData] = useState({
    leaseId: '',
    amount: 0,
    dueDate: '',
    competence: '',
    description: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [sendForm, setSendForm] = useState({
    channels: {
      EMAIL: true,
      SMS: true,
      WHATSAPP: false,
    },
    subject: 'Cobranca de locacao',
    message: '',
    pin: '',
  });
  const [cancelTarget, setCancelTarget] = useState<Charge | null>(null);
  const [cancelForm, setCancelForm] = useState({
    method: 'PIX',
    amount: '',
    reason: '',
    pin: '',
  });
  const [deleteTarget, setDeleteTarget] = useState<Charge | null>(null);
  const [deleteForm, setDeleteForm] = useState({ reason: '', pin: '' });
  const [invoiceTarget, setInvoiceTarget] = useState<string | null>(null);
  const [invoicePin, setInvoicePin] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [chargesRes, leasesRes] = await Promise.all([
        api.get('/charges'),
        api.get('/leases'),
      ]);

      const chargesData = chargesRes.data.data || [];
      const leasesData = leasesRes.data.data || [];

      setCharges(chargesData);
      setLeases(leasesData.filter((l: Lease) => l.code));

      calculateStats(chargesData);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (chargesData: Charge[]) => {
    const pending = chargesData.filter((c) => c.status === 'PENDING');
    const overdue = chargesData.filter((c) => c.status === 'OVERDUE');
    const paid = chargesData.filter((c) => c.status === 'PAID');

    const totalPending = pending.reduce(
      (sum, c) => sum + (parseFloat(c.amount as string) || 0),
      0
    );
    const totalOverdue = overdue.reduce(
      (sum, c) => sum + (parseFloat(c.amount as string) || 0),
      0
    );
    const totalPaid = paid.reduce(
      (sum, c) => sum + (parseFloat(c.amount as string) || 0),
      0
    );

    setStats({
      totalPending,
      totalOverdue,
      totalPaid,
      count: {
        pending: pending.length,
        overdue: overdue.length,
        paid: paid.length,
      },
    });
  };

  const handleOpenModal = () => {
    setFormData({
      leaseId: '',
      amount: 0,
      dueDate: '',
      competence: '',
      description: '',
    });
    setErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setErrors({});
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.leaseId) {
      newErrors.leaseId = 'Contrato é obrigatório';
    }
    if (formData.amount <= 0) {
      newErrors.amount = 'Valor deve ser maior que zero';
    }
    if (!formData.dueDate) {
      newErrors.dueDate = 'Data de vencimento é obrigatória';
    }
    if (!formData.competence) {
      newErrors.competence = 'Competência é obrigatória';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await api.post(`/charges/leases/${formData.leaseId}/generate`, {
        amount: Number(formData.amount),
        dueDate: formData.dueDate,
        competence: formData.competence,
        description: formData.description || undefined,
      });

      await loadData();
      handleCloseModal();
    } catch (error: any) {
      console.error('Erro ao criar cobrança:', error);
      if (error.response?.data?.message) {
        setErrors({ submit: error.response.data.message });
      }
    }
  };

  const user = useAuthStore((s) => s.user);

  const handleDeleteCharge = (charge: Charge) => {
    setDeleteTarget(charge);
    setDeleteForm({ reason: '', pin: '' });
  };

  const handleConfirmDelete = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!deleteTarget) return;
    if (!deleteForm.reason.trim()) {
      alert('Justificativa obrigatória.');
      return;
    }
    if (!/^\d{6}$/.test(deleteForm.pin)) {
      alert('Informe um PIN válido de 6 dígitos.');
      return;
    }
    try {
      await api.delete(`/charges/${deleteTarget.id}`, {
        data: { pin: deleteForm.pin, reason: deleteForm.reason.trim() },
      });
      setDeleteTarget(null);
      await loadData();
      alert('Fatura apagada.');
    } catch (error: any) {
      alert(error?.response?.data?.message || 'Falha ao apagar a fatura.');
    }
  };

  const openCancelModal = (charge: Charge) => {
    setCancelTarget(charge);
    setCancelForm({ method: 'PIX', amount: String(charge.amount ?? ''), reason: '', pin: '' });
  };

  const handleSubmitCancel = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!cancelTarget) return;
    if (!cancelForm.reason.trim()) {
      alert('Informe a justificativa.');
      return;
    }
    if (!/^\d{6}$/.test(cancelForm.pin)) {
      alert('Informe um PIN válido de 6 dígitos.');
      return;
    }
    const amount = parseFloat(cancelForm.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      alert('Informe o valor recebido.');
      return;
    }
    try {
      await api.post(`/charges/${cancelTarget.id}/cancel`, {
        pin: cancelForm.pin,
        reason: cancelForm.reason.trim(),
        method: cancelForm.method,
        amount,
      });
      setCancelTarget(null);
      await loadData();
      alert('Boleto cancelado e recebimento lançado no caixa.');
    } catch (error: any) {
      alert(error?.response?.data?.message || 'Falha ao cancelar/receber.');
    }
  };

  const printCharge = (charge: Charge) => {
    const money = (v: any) =>
      'R$ ' + (parseFloat(v as string) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 });
    const dateBR = (d?: string) => {
      try {
        return d ? format(parseISO(d), 'dd/MM/yyyy', { locale: ptBR }) : '-';
      } catch {
        return d || '-';
      }
    };
    const win = window.open('', '_blank', 'width=820,height=900');
    if (!win) {
      alert('Permita pop-ups para imprimir.');
      return;
    }
    win.document.write(`
      <html><head><title>Fatura ${charge.lease?.code || ''}</title>
      <style>
        body{font-family:Arial,Helvetica,sans-serif;color:#111;padding:32px;}
        h1{font-size:20px;margin:0 0 4px;} .muted{color:#666;font-size:12px;}
        table{width:100%;border-collapse:collapse;margin-top:16px;}
        td,th{border:1px solid #ddd;padding:8px;font-size:13px;text-align:left;}
        .total{font-size:18px;font-weight:bold;margin-top:16px;}
        .box{border:1px solid #ddd;border-radius:8px;padding:16px;margin-top:16px;}
      </style></head><body>
        <h1>Fatura / Cobranca</h1>
        <div class="muted">Contrato ${charge.lease?.code || '-'} &middot; Competencia ${charge.competence || '-'}</div>
        <div class="box">
          <table>
            <tr><th>Imovel</th><td>${charge.lease?.property?.title || '-'}</td></tr>
            <tr><th>Inquilino</th><td>${charge.lease?.tenant?.name || '-'}</td></tr>
            <tr><th>Vencimento</th><td>${dateBR(charge.dueDate)}</td></tr>
            <tr><th>Status</th><td>${charge.status || '-'}</td></tr>
            ${charge.paymentDate ? `<tr><th>Pago em</th><td>${dateBR(charge.paymentDate)}</td></tr>` : ''}
            ${charge.description ? `<tr><th>Descricao</th><td>${charge.description}</td></tr>` : ''}
          </table>
          <div class="total">Valor: ${money(charge.amount)}</div>
        </div>
        <p class="muted" style="margin-top:24px;">Documento gerado pelo MacroImob em ${new Date().toLocaleString('pt-BR')}.</p>
      </body></html>
    `);
    win.document.close();
    win.focus();
    win.print();
  };

  const handleEmitReceipt = async (charge: Charge) => {
    try {
      const { data } = await api.post(`/receipts/charges/${charge.id}/emit`);
      let logoTag = '';
      try {
        const b = await api.get('/branding');
        const url = b.data?.receiptUrl || b.data?.logoUrl;
        if (url) {
          const h = b.data?.sizes?.receiptLogoHeight || 110;
          logoTag = `<img src="${window.location.origin}${url}" style="height:${h}px;max-width:100%;margin-bottom:20px"/><br/>`;
        }
      } catch {
        /* sem logo */
      }
      const esc = (s: string) =>
        (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const win = window.open('', '_blank', 'width=720,height=820');
      if (!win) {
        alert('Permita pop-ups para imprimir o recibo.');
        return;
      }
      win.document.write(
        `<html><head><title>Recibo ${data.number}</title><style>body{font-family:Arial,Helvetica,sans-serif;padding:48px;font-size:14px;line-height:1.7;color:#111}pre{white-space:pre-wrap;font-family:inherit;margin:0}</style></head><body onload="window.print()">${logoTag}<pre>${esc(data.content)}</pre></body></html>`,
      );
      win.document.close();
      win.focus();
    } catch (error: any) {
      alert(error?.response?.data?.message || 'Falha ao emitir recibo.');
    }
  };

  const handleGenerateInvoice = (chargeId: string) => {
    setInvoiceTarget(chargeId);
    setInvoicePin('');
  };

  const handleConfirmInvoice = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!invoiceTarget) return;
    if (!/^\d{6}$/.test(invoicePin)) {
      alert('Informe um PIN válido de 6 dígitos.');
      return;
    }
    try {
      await api.post(`/charges/${invoiceTarget}/generate-invoice`, { pin: invoicePin });
      setInvoiceTarget(null);
      await loadData();
      alert('Fatura gerada com sucesso!');
    } catch (error) {
      alert('Erro ao gerar fatura. Verifique o PIN e tente novamente.');
    }
  };

  const openSendChargeModal = (charge: Charge) => {
    setSelectedChargeForSend(charge);
    setSendForm({
      channels: {
        EMAIL: true,
        SMS: true,
        WHATSAPP: false,
      },
      subject: 'Cobranca de locacao',
      message: '',
      pin: '',
    });
    setShowSendModal(true);
  };

  const handleSendCharge = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedChargeForSend) return;

    const channels = Object.entries(sendForm.channels)
      .filter(([, enabled]) => enabled)
      .map(([channel]) => channel);

    if (channels.length === 0) {
      alert('Selecione ao menos um canal para envio.');
      return;
    }
    if (!/^\d{6}$/.test(sendForm.pin)) {
      alert('Informe um PIN valido de 6 digitos.');
      return;
    }

    try {
      await api.post(`/charges/${selectedChargeForSend.id}/send`, {
        channels,
        subject: sendForm.subject || undefined,
        message: sendForm.message || undefined,
        pin: sendForm.pin,
      });
      alert('Cobranca enviada para os canais selecionados.');
      setShowSendModal(false);
    } catch (error) {
      console.error('Erro ao enviar cobranca:', error);
      alert('Erro ao enviar cobranca. Verifique PIN/canais e tente novamente.');
    }
  };

  const filteredCharges = charges.filter((charge) => {
    const matchesSearch =
      charge.lease?.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      charge.lease?.property?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      charge.lease?.tenant?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      charge.competence?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = filterStatus === 'ALL' || charge.status === filterStatus;

    return matchesSearch && matchesStatus;
  });

  const getStatusConfig = (status: string) => {
    const configs: Record<string, { label: string; color: string; icon: any; bg: string }> = {
      PAID: {
        label: 'Pago',
        color: 'text-green-700',
        bg: 'bg-green-50 border-green-200',
        icon: CheckCircle,
      },
      PENDING: {
        label: 'Pendente',
        color: 'text-yellow-700',
        bg: 'bg-yellow-50 border-yellow-200',
        icon: Clock,
      },
      OVERDUE: {
        label: 'Vencido',
        color: 'text-red-700',
        bg: 'bg-red-50 border-red-200',
        icon: AlertCircle,
      },
      CANCELLED: {
        label: 'Cancelado',
        color: 'text-gray-700',
        bg: 'bg-gray-50 border-gray-200',
        icon: XCircle,
      },
    };
    return configs[status] || configs.PENDING;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-primary-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary-600 to-primary-800 bg-clip-text text-transparent">
            Gestão de Cobranças
          </h1>
          <p className="text-gray-600 mt-1">Gerencie pagamentos e faturas</p>
        </div>
        <button
          onClick={handleOpenModal}
          className="btn-primary flex items-center space-x-2 shadow-lg hover:shadow-xl transition-all"
        >
          <Plus className="w-5 h-5" />
          <span>Nova Cobrança</span>
        </button>
      </div>

      {/* Stats Cards - Glassmorphism */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Pendentes */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-yellow-50 to-yellow-100/50 backdrop-blur-xl border border-yellow-200/50 shadow-xl">
          <div className="absolute top-0 right-0 w-32 h-32 bg-yellow-300/20 rounded-full -mr-16 -mt-16 blur-2xl"></div>
          <div className="relative p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-yellow-500/10 backdrop-blur-sm rounded-xl border border-yellow-500/20">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <span className="text-sm font-medium text-yellow-700 bg-yellow-100 px-3 py-1 rounded-full">
                {stats.count.pending} cobrança{stats.count.pending !== 1 ? 's' : ''}
              </span>
            </div>
            <p className="text-sm text-yellow-700 font-medium mb-1">Total Pendente</p>
            <p className="text-3xl font-bold text-yellow-900">
              R$ {stats.totalPending.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </p>
          </div>
        </div>

        {/* Vencidas */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-red-50 to-red-100/50 backdrop-blur-xl border border-red-200/50 shadow-xl">
          <div className="absolute top-0 right-0 w-32 h-32 bg-red-300/20 rounded-full -mr-16 -mt-16 blur-2xl"></div>
          <div className="relative p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-red-500/10 backdrop-blur-sm rounded-xl border border-red-500/20">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <span className="text-sm font-medium text-red-700 bg-red-100 px-3 py-1 rounded-full">
                {stats.count.overdue} vencida{stats.count.overdue !== 1 ? 's' : ''}
              </span>
            </div>
            <p className="text-sm text-red-700 font-medium mb-1">Total Vencido</p>
            <p className="text-3xl font-bold text-red-900">
              R$ {stats.totalOverdue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </p>
          </div>
        </div>

        {/* Pagas */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-green-50 to-green-100/50 backdrop-blur-xl border border-green-200/50 shadow-xl">
          <div className="absolute top-0 right-0 w-32 h-32 bg-green-300/20 rounded-full -mr-16 -mt-16 blur-2xl"></div>
          <div className="relative p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-green-500/10 backdrop-blur-sm rounded-xl border border-green-500/20">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <span className="text-sm font-medium text-green-700 bg-green-100 px-3 py-1 rounded-full">
                {stats.count.paid} paga{stats.count.paid !== 1 ? 's' : ''}
              </span>
            </div>
            <p className="text-sm text-green-700 font-medium mb-1">Total Recebido</p>
            <p className="text-3xl font-bold text-green-900">
              R$ {stats.totalPaid.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card space-y-4 bg-white/50 backdrop-blur-sm border border-gray-200/50 shadow-lg">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por contrato, imóvel, inquilino ou competência..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-10 bg-white/80 backdrop-blur-sm border-gray-200/50 focus:border-primary-500 focus:ring-primary-500/20"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          {[
            { key: 'ALL', label: 'Todos', icon: FileText },
            { key: 'PENDING', label: 'Pendentes', icon: Clock },
            { key: 'OVERDUE', label: 'Vencidas', icon: AlertCircle },
            { key: 'PAID', label: 'Pagas', icon: CheckCircle },
            { key: 'CANCELLED', label: 'Canceladas', icon: XCircle },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setFilterStatus(key)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all flex items-center space-x-2 ${
                filterStatus === key
                  ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-lg scale-105'
                  : 'bg-white/80 text-gray-700 hover:bg-gray-50 border border-gray-200/50'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Charges List */}
      <div className="space-y-4">
        {filteredCharges.length > 0 ? (
          filteredCharges.map((charge) => {
            const statusConfig = getStatusConfig(charge.status);
            const StatusIcon = statusConfig.icon;

            return (
              <div
                key={charge.id}
                className="relative overflow-hidden rounded-2xl bg-white/60 backdrop-blur-sm border border-gray-200/50 hover:shadow-xl transition-all duration-300 hover:scale-[1.02]"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start space-x-4 flex-1">
                      <div className={`p-3 rounded-xl border ${statusConfig.bg}`}>
                        <StatusIcon className={`w-6 h-6 ${statusConfig.color}`} />
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-lg font-bold text-gray-900">
                            {charge.lease?.code || 'Sem contrato'}
                          </h3>
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold border ${statusConfig.bg} ${statusConfig.color}`}
                          >
                            {statusConfig.label}
                          </span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                          <div className="flex items-center space-x-2 text-gray-600">
                            <Building className="w-4 h-4 text-gray-400" />
                            <span className="line-clamp-1">
                              {charge.lease?.property?.title || 'Imóvel não especificado'}
                            </span>
                          </div>

                          <div className="flex items-center space-x-2 text-gray-600">
                            <User className="w-4 h-4 text-gray-400" />
                            <span className="line-clamp-1">
                              {charge.lease?.tenant?.name || 'Inquilino não especificado'}
                            </span>
                          </div>

                          <div className="flex items-center space-x-2 text-gray-600">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <span>
                              Competência: {charge.competence || 'Não informada'}
                            </span>
                          </div>
                        </div>

                        {charge.description && (
                          <p className="text-sm text-gray-500 mt-2 line-clamp-1">
                            {charge.description}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="text-right ml-4">
                      <p className="text-sm text-gray-500 mb-1">Valor</p>
                      <p className="text-2xl font-bold text-gray-900">
                        R${' '}
                        {(parseFloat(charge.amount as string) || 0).toLocaleString('pt-BR', {
                          minimumFractionDigits: 2,
                        })}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-200/50">
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-4 h-4" />
                        <span>
                          Venc:{' '}
                          {format(parseISO(charge.dueDate), 'dd/MM/yyyy', { locale: ptBR })}
                        </span>
                      </div>
                      {charge.paymentDate && (
                        <div className="flex items-center space-x-1 text-green-600">
                          <CheckCircle className="w-4 h-4" />
                          <span>
                            Pago em:{' '}
                            {format(parseISO(charge.paymentDate), 'dd/MM/yyyy', {
                              locale: ptBR,
                            })}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2 flex-wrap justify-end">
                      {charge.status !== 'PAID' && charge.status !== 'CANCELLED' && (
                        <>
                          <button
                            onClick={() => openSendChargeModal(charge)}
                            className="px-4 py-2 bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors flex items-center space-x-2 text-sm font-medium"
                          >
                            <MessageCircle className="w-4 h-4" />
                            <span>Enviar Cobranca</span>
                          </button>
                          <button
                            onClick={() => handleGenerateInvoice(charge.id)}
                            className="px-4 py-2 bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 transition-colors flex items-center space-x-2 text-sm font-medium"
                          >
                            <CreditCard className="w-4 h-4" />
                            <span>Gerar Fatura</span>
                          </button>
                          {user?.profile !== 'CAPTADOR' && (
                            <button
                              onClick={() => openCancelModal(charge)}
                              className="px-4 py-2 bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 transition-colors flex items-center space-x-2 text-sm font-medium"
                            >
                              <XCircle className="w-4 h-4" />
                              <span>Cancelar/Receber</span>
                            </button>
                          )}
                        </>
                      )}
                      <button
                        onClick={() => printCharge(charge)}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center space-x-2 text-sm font-medium"
                      >
                        <Printer className="w-4 h-4" />
                        <span>Imprimir</span>
                      </button>
                      <button
                        onClick={() => handleEmitReceipt(charge)}
                        className="px-4 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors flex items-center space-x-2 text-sm font-medium"
                      >
                        <FileText className="w-4 h-4" />
                        <span>Recibo</span>
                      </button>
                      {user?.canDeleteCharges && (
                        <button
                          onClick={() => handleDeleteCharge(charge)}
                          className="px-4 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors flex items-center space-x-2 text-sm font-medium"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span>Apagar</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-16 bg-white/60 backdrop-blur-sm rounded-2xl border border-gray-200/50">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">Nenhuma cobrança encontrada</p>
            <p className="text-gray-400 text-sm mt-2">
              {searchTerm || filterStatus !== 'ALL'
                ? 'Tente ajustar os filtros de busca'
                : 'Crie sua primeira cobrança clicando no botão acima'}
            </p>
          </div>
        )}
      </div>

      {/* Modal - Nova Cobrança */}
      <Modal
        isOpen={showModal}
        onClose={handleCloseModal}
        title="Nova Cobrança Manual"
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-700">
                <p className="font-medium mb-1">Dica importante:</p>
                <p>
                  Esta cobrança será criada manualmente. Para cobranças automáticas recorrentes,
                  configure no contrato de locação.
                </p>
              </div>
            </div>
          </div>

          <Select
            label="Contrato de Locação"
            value={formData.leaseId}
            onChange={(e) => setFormData({ ...formData, leaseId: e.target.value })}
            error={errors.leaseId}
            options={leases.map((lease) => ({
              value: lease.id,
              label: `${lease.code} - ${lease.property?.title || 'Imóvel'} - ${
                lease.tenant?.name || 'Inquilino'
              }`,
            }))}
            required
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Valor (R$)"
              type="number"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: Number(e.target.value) })}
              error={errors.amount}
              min="0"
              step="0.01"
              placeholder="0,00"
              required
            />

            <Input
              label="Data de Vencimento"
              type="date"
              value={formData.dueDate}
              onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
              error={errors.dueDate}
              required
            />
          </div>

          <Input
            label="Competência"
            type="text"
            value={formData.competence}
            onChange={(e) => setFormData({ ...formData, competence: e.target.value })}
            error={errors.competence}
            placeholder="Ex: 2025-01 ou Janeiro/2025"
            required
          />

          <Input
            label="Descrição (opcional)"
            type="text"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Ex: Aluguel mensal, Taxa de condomínio, etc."
          />

          {errors.submit && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {errors.submit}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={handleCloseModal}
              className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn-primary px-6 py-2 shadow-lg hover:shadow-xl transition-all"
            >
              Criar Cobrança
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={showSendModal}
        onClose={() => setShowSendModal(false)}
        title="Enviar Cobranca"
        size="md"
      >
        <form onSubmit={handleSendCharge} className="space-y-4">
          <p className="text-sm text-gray-600">
            Selecione os canais para envio da cobranca da competencia{' '}
            <strong>{selectedChargeForSend?.competence || '-'}</strong>.
          </p>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Canais</label>
            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={sendForm.channels.EMAIL}
                  onChange={(event) =>
                    setSendForm((prev) => ({
                      ...prev,
                      channels: { ...prev.channels, EMAIL: event.target.checked },
                    }))
                  }
                />
                <Mail className="w-4 h-4 text-gray-600" />
                <span>Email</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={sendForm.channels.SMS}
                  onChange={(event) =>
                    setSendForm((prev) => ({
                      ...prev,
                      channels: { ...prev.channels, SMS: event.target.checked },
                    }))
                  }
                />
                <Smartphone className="w-4 h-4 text-gray-600" />
                <span>SMS</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={sendForm.channels.WHATSAPP}
                  onChange={(event) =>
                    setSendForm((prev) => ({
                      ...prev,
                      channels: { ...prev.channels, WHATSAPP: event.target.checked },
                    }))
                  }
                />
                <MessageCircle className="w-4 h-4 text-gray-600" />
                <span>WhatsApp</span>
              </label>
            </div>
          </div>

          <Input
            label="Assunto"
            value={sendForm.subject}
            onChange={(event) =>
              setSendForm((prev) => ({ ...prev, subject: event.target.value }))
            }
          />

          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">
              Mensagem personalizada (opcional)
            </label>
            <textarea
              className="input min-h-[90px]"
              value={sendForm.message}
              onChange={(event) =>
                setSendForm((prev) => ({ ...prev, message: event.target.value }))
              }
            />
          </div>

          <Input
            label="PIN de seguranca (6 digitos)"
            type="password"
            value={sendForm.pin}
            onChange={(event) =>
              setSendForm((prev) => ({ ...prev, pin: event.target.value }))
            }
            required
          />

          <div className="flex justify-end gap-3 pt-2 border-t">
            <button
              type="button"
              onClick={() => setShowSendModal(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button type="submit" className="btn-primary">
              Enviar
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        title="Cancelar boleto e registrar recebimento"
        size="md"
      >
        <form onSubmit={handleSubmitCancel} className="space-y-4">
          <p className="text-sm text-gray-600">
            Cobranca <strong>{cancelTarget?.lease?.code || '-'}</strong> &middot; competencia{' '}
            <strong>{cancelTarget?.competence || '-'}</strong>. O boleto sera cancelado e o valor
            entra no caixa pela forma escolhida.
          </p>
          <Select
            label="Forma de recebimento"
            value={cancelForm.method}
            onChange={(e) => setCancelForm((p) => ({ ...p, method: e.target.value }))}
            options={[
              { value: 'PIX', label: 'PIX' },
              { value: 'BANK_TRANSFER', label: 'Deposito / Transferencia' },
              { value: 'CASH', label: 'Dinheiro (especie)' },
              { value: 'CREDIT_CARD', label: 'Cartao de credito' },
              { value: 'DEBIT_CARD', label: 'Cartao de debito' },
            ]}
          />
          <Input
            label="Valor recebido (R$)"
            type="number"
            step="0.01"
            value={cancelForm.amount}
            onChange={(e) => setCancelForm((p) => ({ ...p, amount: e.target.value }))}
            required
          />
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">
              Justificativa (obrigatoria)
            </label>
            <textarea
              className="input min-h-[80px]"
              value={cancelForm.reason}
              onChange={(e) => setCancelForm((p) => ({ ...p, reason: e.target.value }))}
              placeholder="Ex.: Cliente pagou via PIX para evitar os juros do boleto."
              required
            />
          </div>
          <Input
            label="PIN financeiro (6 digitos)"
            type="password"
            value={cancelForm.pin}
            onChange={(e) => setCancelForm((p) => ({ ...p, pin: e.target.value }))}
            required
          />
          <div className="flex justify-end gap-3 pt-2 border-t">
            <button
              type="button"
              onClick={() => setCancelTarget(null)}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Voltar
            </button>
            <button type="submit" className="btn-primary">
              Confirmar recebimento
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Apagar cobranca"
        size="md"
      >
        <form onSubmit={handleConfirmDelete} className="space-y-4">
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            Isso remove a fatura <strong>{deleteTarget?.lease?.code || ''}</strong> e TUDO que ela
            gerou (itens, pagamentos, repasses e boleto). Acao irreversivel.
          </div>
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">Justificativa (obrigatoria)</label>
            <textarea
              className="input min-h-[70px]"
              value={deleteForm.reason}
              onChange={(e) => setDeleteForm((p) => ({ ...p, reason: e.target.value }))}
              required
            />
          </div>
          <Input
            label="PIN financeiro (6 digitos)"
            type="password"
            inputMode="numeric"
            autoComplete="off"
            value={deleteForm.pin}
            onChange={(e) => setDeleteForm((p) => ({ ...p, pin: e.target.value }))}
            required
          />
          <div className="flex justify-end gap-3 pt-2 border-t">
            <button
              type="button"
              onClick={() => setDeleteTarget(null)}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Voltar
            </button>
            <button type="submit" className="px-4 py-2 bg-red-600 text-white rounded-lg">
              Apagar
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={!!invoiceTarget}
        onClose={() => setInvoiceTarget(null)}
        title="Gerar fatura"
        size="sm"
      >
        <form onSubmit={handleConfirmInvoice} className="space-y-4">
          <p className="text-sm text-gray-600">Confirme com seu PIN financeiro para emitir a fatura.</p>
          <Input
            label="PIN financeiro (6 digitos)"
            type="password"
            inputMode="numeric"
            autoComplete="off"
            value={invoicePin}
            onChange={(e) => setInvoicePin(e.target.value)}
            required
          />
          <div className="flex justify-end gap-3 pt-2 border-t">
            <button
              type="button"
              onClick={() => setInvoiceTarget(null)}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Voltar
            </button>
            <button type="submit" className="btn-primary">
              Gerar fatura
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
